import { useState } from 'react'
import { Swiper, SwiperSlide, SwiperClass } from 'swiper/react'
import {
    Navigation,
    Pagination,
    Thumbs,
    FreeMode,
    EffectFade,
} from 'swiper/modules'
import Icon from 'components/shared/Icon'
import Box from 'components/shared/Box'
import Modal from 'components/shared/Modal'
import { Image } from 'components/shared/Image'
import { useResolutions } from 'store/resolutions'
import {
    GalleryMobileWrapper,
    GallerySliderWrapper,
    GalleryThumbnailsWrapper,
} from './GallerySliderModal.styles'
import { GallerySliderModalProps } from './GallerySliderModal.types'

import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/thumbs'
import 'swiper/css/free-mode'
import 'swiper/css/effect-fade'

export const GallerySliderModal = ({
    title,
    images,
    onClose,
}: GallerySliderModalProps) => {
    const { isMobile } = useResolutions()

    const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass>()

    return (
        <Modal
            isOpened={true}
            maxWidth={770}
            onClose={onClose}
            isMobileFullscreen
            hasGlobalHeader
            headerMessage={title}
            testIdPrefix="gallery-slider-modal"
        >
            <Box gap={8} padding={[8, 40]} bgColor="grayscale_200">
                {/* mobile: */}
                {isMobile ? (
                    <GalleryMobileWrapper gap={8}>
                        {images.map((imageUrl) => (
                            <Image
                                src={imageUrl}
                                alt={title}
                                width="100%"
                                height="239px"
                                borderRadius={8}
                                key={imageUrl}
                            />
                        ))}
                    </GalleryMobileWrapper>
                ) : (
                    <>
                        {/* desktop: */}
                        {/* main gallery slider */}
                        <GallerySliderWrapper>
                            <Swiper
                                navigation={{
                                    prevEl: '.swiper-btn-prev',
                                    nextEl: '.swiper-btn-next',
                                }}
                                pagination={{
                                    type: 'fraction',
                                }}
                                thumbs={{ swiper: thumbsSwiper }}
                                effect={'fade'}
                                modules={[
                                    Navigation,
                                    Pagination,
                                    Thumbs,
                                    FreeMode,
                                    EffectFade,
                                ]}
                            >
                                {images.map((imageUrl, index) => (
                                    <SwiperSlide key={index}>
                                        <Image
                                            src={imageUrl}
                                            alt={title}
                                            height="100%"
                                        />
                                    </SwiperSlide>
                                ))}
                                <button
                                    className="swiper-btn-prev"
                                    data-testid="gallery-slider-modal-btn-prev"
                                >
                                    <Icon
                                        variant="chevron"
                                        color="grayscale_900"
                                        size={16}
                                        rotation={90}
                                    />
                                </button>
                                <button
                                    className="swiper-btn-next"
                                    data-testid="gallery-slider-modal-btn-next"
                                >
                                    <Icon
                                        variant="chevron"
                                        color="grayscale_900"
                                        size={16}
                                        rotation={270}
                                    />
                                </button>
                            </Swiper>
                        </GallerySliderWrapper>

                        {/* slider with thumbnails */}
                        {/* - hide if there is only one image */}
                        {images.length > 1 && (
                            <GalleryThumbnailsWrapper>
                                <Swiper
                                    onSwiper={setThumbsSwiper}
                                    spaceBetween={12}
                                    slidesPerView="auto"
                                    freeMode={true}
                                    watchSlidesProgress={true}
                                    modules={[FreeMode, Navigation, Thumbs]}
                                >
                                    {images.map((imageUrl, index) => (
                                        <SwiperSlide key={index}>
                                            <Image
                                                src={imageUrl}
                                                alt={title}
                                                width={100}
                                                height={100}
                                            />
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                            </GalleryThumbnailsWrapper>
                        )}
                    </>
                )}
            </Box>
        </Modal>
    )
}
